/**
 * El ORDEN de los vehículos del sitio público, en un solo lugar (ticket #164).
 *
 * Antes cada superficie se ordenaba sola: la consulta del store pedía
 * `created_at.desc`, la precarga del `<head>` repetía ese string a mano, y cada
 * grilla tenía su propio comparador copiado — cinco implementaciones de lo
 * mismo. Hacer que el orden dependa de una configuración, en cinco lugares, es
 * garantizar que un día dejen de coincidir. La decisión vive acá.
 *
 * ── La regla ────────────────────────────────────────────────────────────────
 *
 * ORDEN INICIAL (con el que abre la página): lo elige la AUTOMOTORA desde
 * Configuración, en `clients.web_vehicle_order`:
 *
 *     'newest'  → created_at DESC → id DESC   (el default; lo de siempre)
 *     'oldest'  → created_at ASC  → id ASC
 *
 * El `id` sólo desempata dos autos cargados en el mismo instante, que hoy
 * salen en un orden arbitrario de Postgres.
 *
 * ORDEN ELEGIDO POR EL VISITANTE (precio, fecha, año o kilometraje, desde el
 * desplegable del sitio): manda su elección, y la configuración de la
 * automotora no participa. Si alguien pide "precio menor a mayor" y le
 * devolvemos otra cosa, la pantalla se ve rota.
 */

/** Lo que la automotora configura. Espejo de `clients.web_vehicle_order`. */
export type OrdenWebTenant = 'newest' | 'oldest';

/** El valor histórico, y el que vale si la columna todavía no existe. */
export const ORDEN_TENANT_POR_DEFECTO: OrdenWebTenant = 'newest';

/**
 * Normaliza lo que venga de la fila del tenant. `undefined` es el caso real
 * mientras la migración 20261202120000 no esté aplicada: ahí vale el histórico.
 */
export const ordenDelTenant = (valor: unknown): OrdenWebTenant =>
  valor === 'oldest' ? 'oldest' : ORDEN_TENANT_POR_DEFECTO;

/** Lo mínimo que hace falta de un vehículo para ordenarlo. */
export interface VehiculoOrdenable {
  id?: number | string | null;
  created_at?: string | null;
  price?: number | null;
  discount_percentage?: number | null;
  year?: number | null;
  mileage?: number | null;
}

/**
 * Valor del desplegable de orden.
 *
 * `'default'` es el orden INICIAL —el que configuró la automotora— y es el
 * valor con el que arranca el store. No se ofrece como opción en el selector:
 * con él, el botón muestra su rótulo genérico sin marcar nada, igual que antes
 * del ticket. Los otros son elecciones explícitas del visitante.
 */
export type OrdenVehiculos =
  | 'default'
  | 'price_asc'
  | 'price_desc'
  | 'date_asc'
  | 'date_desc'
  | 'year_desc'
  | 'year_asc'
  | 'mileage_asc'
  | 'mileage_desc';

export const ORDEN_INICIAL: OrdenVehiculos = 'default';

/**
 * Columnas del orden inicial, para `.order()` de supabase-js.
 *
 * `web_priority` DESC va PRIMERO: la prioridad es por vehículo y el criterio de
 * la automotora desempata dentro de cada nivel. Con todo en 0 —el estado de
 * hoy— esta primera clave no decide nada y el orden es el de siempre.
 */
export const ordenInventario = (tenant: OrdenWebTenant) => {
  const ascendente = tenant === 'oldest';
  return [
    { column: 'web_priority', ascending: false },
    { column: 'created_at', ascending: ascendente },
    { column: 'id', ascending: ascendente },
  ] as const;
};

/**
 * El mismo orden como string de PostgREST, para la precarga del `<head>`, que
 * arma la URL REST a mano. Tiene que ser equivalente al de arriba o la
 * respuesta precargada vendría ordenada distinto que la del store.
 */
export const ordenInventarioRest = (tenant: OrdenWebTenant): string =>
  ordenInventario(tenant)
    .map((o) => `${o.column}.${o.ascending ? 'asc' : 'desc'}`)
    .join(',');

const fecha = (v: VehiculoOrdenable): number => {
  const t = v.created_at ? new Date(v.created_at).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};

const identificador = (v: VehiculoOrdenable): number => {
  const n = Number(v.id);
  return Number.isFinite(n) ? n : 0;
};

/** Precio con el descuento aplicado, que es el que ve el visitante. */
export const precioVisible = (v: VehiculoOrdenable): number | null => {
  if (v.price === undefined || v.price === null) return null;
  const pct = v.discount_percentage;
  return pct && pct > 0 ? v.price * (1 - pct / 100) : v.price;
};

/**
 * Prioridad web del vehículo, tal como la guarda el admin en
 * `vehicles.web_priority`: -1 Baja, 0 Normal, 1 Alta, 2 Muy alta.
 *
 * Ausente o ilegible = 0 (Normal). Eso es lo que hace que el cambio sea
 * invisible hasta que alguien configure algo: hoy las 8.207 filas están en 0.
 */
export const prioridadDe = (v: VehiculoOrdenable): number => {
  const n = Number((v as { web_priority?: unknown }).web_priority);
  return Number.isFinite(n) ? n : 0;
};

/**
 * El comparador del orden INICIAL (ticket #164). Dos pasos, en este orden:
 *
 *   1. `web_priority` DESC — Muy alta → Alta → Normal → Baja.
 *   2. el criterio que configuró la automotora (`web_vehicle_order`), que
 *      dentro de cada nivel ordena por `created_at` y desempata por `id`.
 *
 * Con TODO en Normal —el estado de hoy— el paso 1 nunca decide nada y el
 * resultado es exactamente el de antes de la prioridad. Es la propiedad que
 * hace que esto no cambie ninguna web hasta que alguien lo use.
 *
 * Exportado como fábrica porque el carrusel y `/vehicles` tienen reglas propias
 * ANTERIORES (mostrar primero lo publicado / lo disponible) y necesitan este
 * criterio como paso siguiente, no como función entera.
 */
export const compararInicial =
  (tenant: OrdenWebTenant = ORDEN_TENANT_POR_DEFECTO) =>
  (a: VehiculoOrdenable, b: VehiculoOrdenable): number => {
    // Mayor prioridad primero, siempre, sin importar lo que configuró el
    // tenant: la prioridad es por vehículo y la configuración es el criterio de
    // DESEMPATE dentro de cada nivel.
    const porPrioridad = prioridadDe(b) - prioridadDe(a);
    if (porPrioridad !== 0) return porPrioridad;
    const porFecha = compararFechaDelTenant(tenant)(a, b);
    if (porFecha !== 0) return porFecha;
    const signo = tenant === 'oldest' ? 1 : -1;
    return (identificador(a) - identificador(b)) * signo;
  };

/**
 * Sólo la FECHA, en el sentido que configuró la automotora, sin el desempate
 * por `id`.
 *
 * Lo usa el carrusel, que tiene su propia regla para los empates de fecha
 * (precio más bajo primero) y es anterior a este ticket. Con `compararInicial`
 * entero, el desempate por `id` se metía antes que el precio y mataba esa
 * regla — invisible en la mayoría de las automotoras, pero no en las que
 * cargaron su stock de una sola vez y tienen todo el mismo `created_at`.
 */
export const compararFechaDelTenant =
  (tenant: OrdenWebTenant = ORDEN_TENANT_POR_DEFECTO) =>
  (a: VehiculoOrdenable, b: VehiculoOrdenable): number => {
    const signo = tenant === 'oldest' ? 1 : -1;
    return (fecha(a) - fecha(b)) * signo;
  };

/**
 * Sólo la PRIORIDAD, de mayor a menor. Mismo motivo que el de arriba: el
 * carrusel necesita encadenar prioridad → fecha → su regla propia de precio, y
 * `compararInicial` entero mete el desempate por `id` antes del precio.
 */
export const compararPrioridad = (
  a: VehiculoOrdenable,
  b: VehiculoOrdenable
): number => prioridadDe(b) - prioridadDe(a);

/**
 * El comparador de CUALQUIER orden, que es el que usan las cuatro superficies.
 *
 * Existe como fábrica —y no sólo como `ordenarVehiculos`— porque /vehicles, el
 * carrusel y `VehicleList` tienen una regla propia ANTERIOR (disponibilidad,
 * "Publicado" primero, vendidos al final) y necesitan este criterio como paso
 * siguiente. Antes cada uno repetía el `switch` a mano, y los tres lo repetían
 * SIN DESEMPATE: con `return 0` el resultado quedaba a merced de que la lista
 * llegara ya ordenada. En GoAuto Dev, donde los autos entraron en una carga
 * masiva y comparten `created_at`, eso hacía que elegir "más recientes" no
 * cambiara nada: la pantalla ignoraba al visitante en silencio.
 *
 * Toda rama termina desempatando en el orden inicial, así que el resultado es
 * total y estable: dos autos con el mismo precio no cambian de lugar entre
 * renders.
 */
export const compararPorOrden = (
  orden: OrdenVehiculos = ORDEN_INICIAL,
  tenant: OrdenWebTenant = ORDEN_TENANT_POR_DEFECTO
) => {
  const inicial = compararInicial(tenant);

  if (orden === 'default') return inicial;

  if (orden === 'price_asc' || orden === 'price_desc') {
    const signo = orden === 'price_asc' ? 1 : -1;
    return (a: VehiculoOrdenable, b: VehiculoOrdenable): number => {
      const pa = precioVisible(a);
      const pb = precioVisible(b);
      // Sin precio siempre al final, en los dos sentidos.
      if (pa === null && pb === null) return inicial(a, b);
      if (pa === null) return 1;
      if (pb === null) return -1;
      if (pa !== pb) return (pa - pb) * signo;
      return inicial(a, b);
    };
  }

  if (orden === 'year_asc' || orden === 'year_desc') {
    const signo = orden === 'year_asc' ? 1 : -1;
    return (a: VehiculoOrdenable, b: VehiculoOrdenable): number => {
      const diferencia = ((a.year ?? 0) - (b.year ?? 0)) * signo;
      return diferencia !== 0 ? diferencia : inicial(a, b);
    };
  }

  if (orden === 'mileage_asc' || orden === 'mileage_desc') {
    const signo = orden === 'mileage_asc' ? 1 : -1;
    return (a: VehiculoOrdenable, b: VehiculoOrdenable): number => {
      // Sin kilometraje al final en los dos sentidos, como hacía la grilla v2
      // con MAX_SAFE_INTEGER.
      const ka = a.mileage ?? null;
      const kb = b.mileage ?? null;
      if (ka === null && kb === null) return inicial(a, b);
      if (ka === null) return 1;
      if (kb === null) return -1;
      if (ka !== kb) return (ka - kb) * signo;
      return inicial(a, b);
    };
  }

  // date_asc | date_desc: la fecha que pidió el visitante. Es una elección
  // explícita, así que manda sobre lo que configuró la automotora — incluido el
  // desempate, que acompaña al sentido que pidió y no al del tenant.
  const signo = orden === 'date_asc' ? 1 : -1;
  return (a: VehiculoOrdenable, b: VehiculoOrdenable): number => {
    const diferencia = (fecha(a) - fecha(b)) * signo;
    if (diferencia !== 0) return diferencia;
    return (identificador(a) - identificador(b)) * signo;
  };
};

/**
 * Ordena una lista ya filtrada según lo que eligió el visitante, cayendo en el
 * orden configurado por la automotora cuando no eligió nada. No muta: devuelve
 * una copia.
 */
export const ordenarVehiculos = <T extends VehiculoOrdenable>(
  vehiculos: readonly T[],
  orden: OrdenVehiculos = ORDEN_INICIAL,
  tenant: OrdenWebTenant = ORDEN_TENANT_POR_DEFECTO
): T[] => [...vehiculos].sort(compararPorOrden(orden, tenant));
